/**
 * Authentication Service
 * Handles all Firebase authentication operations
 * Provides reusable auth methods across the application
 */

import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  updateProfile
} from 'firebase/auth';
import type { User, AuthError } from 'firebase/auth';
import { auth } from '../firebase';
import { authApi } from '@/api/services/authApi';
import { saveTokens, clearTokens } from '@/utils/storage';
import type { AuthContextUser } from '@/types/api';

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account' // Always show account picker
});

/**
 * Auth Service
 * Centralized authentication operations
 */
export const authService = {
  /**
   * Sign in with Google OAuth
   * Opens popup for Google account selection
   * Then authenticates with backend API
   */
  async signInWithGoogle(selectedRole: 'tutor' | 'parent'): Promise<AuthContextUser> {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      // Authenticate with backend
      const backendData = await authApi.authenticateUser({
        firebaseUid: result.user.uid,
        email: result.user.email || '',
        loginMethod: 'google',
        selectedRole,
      });

      // Save JWT tokens
      saveTokens(backendData.jwtToken, backendData.refreshToken);

      // Return combined user data with profile if available
      return {
        user: backendData.user,
        tokens: {
          accessToken: backendData.jwtToken,
          refreshToken: backendData.refreshToken,
        },
        tutorProfile: backendData.tutorProfile,
        parentProfile: backendData.parentProfile,
      };
    } catch (error) {
      // Handle specific Firebase errors
      const authError = error as AuthError;
      if (authError.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled. Please try again.');
      }
      if (authError.code === 'auth/popup-blocked') {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }
      if (authError.code === 'auth/cancelled-popup-request') {
        throw new Error('Another sign-in popup is already open.');
      }
      throw error;
    }
  },

  /**
   * Sign up with email and password
   * Creates new user account and authenticates with backend
   * Backend automatically generates and sends OTP email
   */
  async signUpWithEmail(email: string, password: string, selectedRole: 'tutor' | 'parent'): Promise<AuthContextUser> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Authenticate with backend
      // Note: Backend authenticate endpoint already sends OTP via Brevo as fire-and-forget
      const backendData = await authApi.authenticateUser({
        firebaseUid: result.user.uid,
        email: email,
        loginMethod: 'email_password',
        selectedRole,
      });

      // Save JWT tokens
      saveTokens(backendData.jwtToken, backendData.refreshToken);

      // Return combined user data with profile if available
      return {
        user: backendData.user,
        tokens: {
          accessToken: backendData.jwtToken,
          refreshToken: backendData.refreshToken,
        },
        tutorProfile: backendData.tutorProfile,
        parentProfile: backendData.parentProfile,
      };
    } catch (error) {
      const authError = error as AuthError;
      if (authError.code === 'auth/email-already-in-use') {
        throw new Error('This email is already registered. Please log in instead.');
      }
      if (authError.code === 'auth/weak-password') {
        throw new Error('Password is too weak. Use at least 6 characters.');
      }
      if (authError.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      }
      throw error;
    }
  },

  /**
   * Sign in with email and password
   * Authenticates existing user and backend
   */
  async signInWithEmail(email: string, password: string, selectedRole: 'tutor' | 'parent'): Promise<AuthContextUser> {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Authenticate with backend
      const backendData = await authApi.authenticateUser({
        firebaseUid: result.user.uid,
        email: email,
        loginMethod: 'email_password',
        selectedRole,
      });

      // Save JWT tokens
      saveTokens(backendData.jwtToken, backendData.refreshToken);

      // Return combined user data with profile if available
      return {
        user: backendData.user,
        tokens: {
          accessToken: backendData.jwtToken,
          refreshToken: backendData.refreshToken,
        },
        tutorProfile: backendData.tutorProfile,
        parentProfile: backendData.parentProfile,
      };
    } catch (error) {
      const authError = error as AuthError;
      if (authError.code === 'auth/user-not-found') {
        throw new Error('No account found with this email. Please sign up.');
      }
      if (authError.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      }
      if (authError.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      }
      if (authError.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      }
      if (authError.code === 'auth/invalid-credential') {
        throw new Error('Invalid email or password. Please try again.');
      }
      throw error;
    }
  },

  /**
   * Update user profile information
   * Used after registration to add displayName
   */
  async updateUserProfile(user: User, displayName: string, photoURL?: string): Promise<void> {
    try {
      await updateProfile(user, {
        displayName,
        ...(photoURL && { photoURL })
      });
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to update profile');
    }
  },

  /**
   * Send email verification
   * Resends verification email to current user via backend (Brevo)
   */
  async sendVerificationEmail(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in');
      }
      if (!user.email) {
        throw new Error('User email not available');
      }
      if (user.emailVerified) {
        throw new Error('Email is already verified');
      }
      await authApi.sendVerificationEmail(user.email);
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to send verification email');
    }
  },

  /**
   * Send password reset email
   * Sends a password reset link via backend (Brevo)
   * Always succeeds (doesn't reveal if email exists)
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await authApi.sendPasswordResetEmail(email);
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to send password reset email');
    }
  },

  /**
   * Verify email with 6-digit OTP
   * Returns tokens on successful verification
   */
  async verifyEmailOTP(email: string, otp: string): Promise<AuthContextUser> {
    try {
      const backendData = await authApi.verifyOTP(email, otp);
      
      // Save JWT tokens
      saveTokens(backendData.jwtToken, backendData.refreshToken);

      return {
        user: backendData.user,
        tokens: {
          accessToken: backendData.jwtToken,
          refreshToken: backendData.refreshToken,
        },
        tutorProfile: backendData.tutorProfile,
        parentProfile: backendData.parentProfile,
      };
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'OTP verification failed');
    }
  },

  /**
   * Resend email verification OTP
   */
  async resendVerificationOTP(email: string): Promise<void> {
    try {
      await authApi.resendVerificationOTP(email);
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to resend verification OTP');
    }
  },

  /**
   * Reset password with OTP
   */
  async resetPasswordWithOTP(email: string, otp: string, newPassword: string): Promise<void> {
    try {
      await authApi.resetPasswordWithOTP(email, otp, newPassword);
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Password reset failed');
    }
  },

  /**
   * Sign out current user
   * Clears Firebase session and stored tokens
   */
  async signOut(): Promise<void> {
    try {
      // Best-effort server-side token invalidation (increments tokenVersion)
      try { await authApi.logout(); } catch { /* continue with local cleanup */ }
      await firebaseSignOut(auth);
      // Clear stored tokens
      clearTokens();
      // Clear stored role
      localStorage.removeItem('client_userRole');
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to sign out');
    }
  },

  /**
   * Switch the active role
   * Calls backend, saves new tokens, returns updated data
   */
  async switchRole(newRole: 'tutor' | 'parent') {
    const data = await authApi.switchRole(newRole);
    saveTokens(data.jwtToken, data.refreshToken);
    return data;
  },

  /**
   * Get current user
   * Returns null if no user is signed in
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  },

  /**
   * Check if email is verified
   */
  isEmailVerified(): boolean {
    return auth.currentUser?.emailVerified ?? false;
  }
};
